import { useState, useEffect, useCallback } from 'react';
import {
  CommunicationMetrics,
  EmailTemplate,
  SMSTemplate,
  DeliveryLog,
  CommunicationFilters,
  PaginatedResponse,
  CreateEmailTemplateData,
  CreateSMSTemplateData,
  UpdateEmailTemplateData,
  UpdateSMSTemplateData,
} from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useCommunications = () => {
  const [metrics, setMetrics] = useState<CommunicationMetrics | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSMSTemplates] = useState<SMSTemplate[]>([]);
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([]);
  const [deliveryLogsPage, setDeliveryLogsPage] = useState(1);
  const [deliveryLogsPageSize] = useState(50);
  const [deliveryLogsTotalCount, setDeliveryLogsTotalCount] = useState(0);
  const [deliveryLogsLoading, setDeliveryLogsLoading] = useState(false);
  const [filters, setFilters] = useState<CommunicationFilters>({
    search: '',
    delivery_type: 'all',
    status: 'all',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await ApiClient.get('/communications/stats/');

      if (!response.ok) {
        throw new Error(`Failed to fetch communications data: ${response.status}`);
      }

      const result = await handleApiResult<CommunicationMetrics>(
        response,
        'Failed to fetch communication metrics'
      );

      if (result.success && result.data) {
        setMetrics(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch communications data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch communications data');
      throw err; // Re-throw to be caught by fetchData
    }
  }, []);

  const fetchEmailTemplates = useCallback(async () => {
    try {
      const response = await ApiClient.get('/communications/email-templates/');
      const result = await handleApiResult<EmailTemplate[]>(
        response,
        'Failed to fetch email templates'
      );

      if (result.success && result.data) {
        setEmailTemplates(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch email templates');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch email templates');
      setError(processedErrors.generalError || 'Failed to fetch email templates');
    }
  }, []);

  const fetchSMSTemplates = useCallback(async () => {
    try {
      const response = await ApiClient.get('/communications/sms-templates/');
      const result = await handleApiResult<SMSTemplate[]>(
        response,
        'Failed to fetch SMS templates'
      );

      if (result.success && result.data) {
        setSMSTemplates(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch SMS templates');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch SMS templates');
      setError(processedErrors.generalError || 'Failed to fetch SMS templates');
    }
  }, []);

  const fetchDeliveryLogs = useCallback(
    async (showLoading = false) => {
      try {
        if (showLoading) {
          setDeliveryLogsLoading(true);
        }
        const params = new URLSearchParams();
        if (filters.delivery_type !== 'all') {
          params.append('delivery_type', filters.delivery_type);
        }
        if (filters.status !== 'all') {
          params.append('status', filters.status);
        }
        if (filters.search) {
          params.append('search', filters.search);
        }
        params.append('page', deliveryLogsPage.toString());
        params.append('page_size', deliveryLogsPageSize.toString());

        const response = await ApiClient.get(`/communications/delivery-logs/?${params.toString()}`);
        const result = await handleApiResult<PaginatedResponse<DeliveryLog>>(
          response,
          'Failed to fetch delivery logs'
        );

        if (result.success && result.data) {
          setDeliveryLogs(result.data.results || []);
          setDeliveryLogsTotalCount(result.data.count || 0);
        } else {
          setError(result.errors?.generalError || 'Failed to fetch delivery logs');
        }
      } catch (err) {
        const processedErrors = handleApiCatch(err, 'Failed to fetch delivery logs');
        setError(processedErrors.generalError || 'Failed to fetch delivery logs');
      } finally {
        if (showLoading) {
          setDeliveryLogsLoading(false);
        }
      }
    },
    [filters, deliveryLogsPage, deliveryLogsPageSize]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchMetrics(), fetchEmailTemplates(), fetchSMSTemplates()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch communications data');
    } finally {
      setLoading(false);
    }
  }, [fetchMetrics, fetchEmailTemplates, fetchSMSTemplates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (loading) {
      fetchDeliveryLogs(false);
    } else {
      fetchDeliveryLogs(true);
    }
  }, [filters, deliveryLogsPage, deliveryLogsPageSize, loading, fetchDeliveryLogs]);

  useEffect(() => {
    setDeliveryLogsPage(1);
  }, [filters]);

  const createEmailTemplate = async (data: CreateEmailTemplateData) => {
    try {
      setLoading(true);
      const response = await ApiClient.post('/communications/email-templates/', data);
      const result = await handleApiResult<EmailTemplate>(
        response,
        'Failed to create email template'
      );

      if (result.success && result.data) {
        setEmailTemplates(prev => [...prev, result.data!]);
      } else {
        setError(result.errors?.generalError || 'Failed to create email template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to create email template');
      setError(processedErrors.generalError || 'Failed to create email template');
    } finally {
      setLoading(false);
    }
  };

  const createSMSTemplate = async (data: CreateSMSTemplateData) => {
    try {
      setLoading(true);
      const response = await ApiClient.post('/communications/sms-templates/', data);
      const result = await handleApiResult<SMSTemplate>(response, 'Failed to create SMS template');

      if (result.success && result.data) {
        setSMSTemplates(prev => [...prev, result.data!]);
      } else {
        setError(result.errors?.generalError || 'Failed to create SMS template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to create SMS template');
      setError(processedErrors.generalError || 'Failed to create SMS template');
    } finally {
      setLoading(false);
    }
  };

  const updateEmailTemplate = async (id: string, data: UpdateEmailTemplateData) => {
    try {
      setLoading(true);
      const response = await ApiClient.patch(`/communications/email-templates/${id}/`, data);
      const result = await handleApiResult<EmailTemplate>(
        response,
        'Failed to update email template'
      );

      if (result.success && result.data) {
        setEmailTemplates(prev =>
          prev.map(template => (template.id === id ? result.data! : template))
        );
      } else {
        setError(result.errors?.generalError || 'Failed to update email template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to update email template');
      setError(processedErrors.generalError || 'Failed to update email template');
    } finally {
      setLoading(false);
    }
  };

  const updateSMSTemplate = async (id: string, data: UpdateSMSTemplateData) => {
    try {
      setLoading(true);
      const response = await ApiClient.patch(`/communications/sms-templates/${id}/`, data);
      const result = await handleApiResult<SMSTemplate>(response, 'Failed to update SMS template');

      if (result.success && result.data) {
        setSMSTemplates(prev =>
          prev.map(template => (template.id === id ? result.data! : template))
        );
      } else {
        setError(result.errors?.generalError || 'Failed to update SMS template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to update SMS template');
      setError(processedErrors.generalError || 'Failed to update SMS template');
    } finally {
      setLoading(false);
    }
  };

  const deleteEmailTemplate = async (id: string) => {
    try {
      setLoading(true);
      const response = await ApiClient.delete(`/communications/email-templates/${id}/`);

      if (response.ok) {
        setEmailTemplates(prev => prev.filter(template => template.id !== id));
      } else {
        const { handleApiError } = await import('../../../utils/errorHandler');
        const processedErrors = await handleApiError(response);
        setError(processedErrors.generalError || 'Failed to delete email template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to delete email template');
      setError(processedErrors.generalError || 'Failed to delete email template');
    } finally {
      setLoading(false);
    }
  };

  const deleteSMSTemplate = async (id: string) => {
    try {
      setLoading(true);
      const response = await ApiClient.delete(`/communications/sms-templates/${id}/`);

      if (response.ok) {
        setSMSTemplates(prev => prev.filter(template => template.id !== id));
      } else {
        const { handleApiError } = await import('../../../utils/errorHandler');
        const processedErrors = await handleApiError(response);
        setError(processedErrors.generalError || 'Failed to delete SMS template');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to delete SMS template');
      setError(processedErrors.generalError || 'Failed to delete SMS template');
    } finally {
      setLoading(false);
    }
  };

  const viewDeliveryLog = (_id: string) => {
    // View functionality not implemented yet
  };

  return {
    metrics,
    emailTemplates,
    smsTemplates,
    deliveryLogs,
    deliveryLogsPage,
    deliveryLogsPageSize,
    deliveryLogsTotalCount,
    deliveryLogsLoading,
    setDeliveryLogsPage,
    filters,
    setFilters,
    loading,
    error,
    createEmailTemplate,
    createSMSTemplate,
    updateEmailTemplate,
    updateSMSTemplate,
    deleteEmailTemplate,
    deleteSMSTemplate,
    viewDeliveryLog,
    refetch: fetchData,
  };
};
